import { Close as CloseIcon, Info as InfoIcon, Launch as LaunchIcon, VpnKey as VpnIcon } from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomDialog from "../../../../SharedComponents/CustomDialog";
import { KustoExplorerDialogProps } from "../../Interfaces/PromptPlayground.interfaces";

export const KustoExplorerDialog: React.FC<KustoExplorerDialogProps> = ({
  onClose,
  onRedirectComplete,
  open,
  query
}) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setIsRedirecting(false);
      setCountdown(10);
    }
  }, [open]);

  // Countdown timer
  useEffect(() => {
    if (!open || isRedirecting) return;

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setIsRedirecting(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [open, isRedirecting]);

  // Handle redirect
  useEffect(() => {
    if (!open || !isRedirecting) return;

    const timer = setTimeout(() => {
      console.log("Redirecting to Azure Data Explorer with query:", query);
      onRedirectComplete?.();
      onClose();
    }, 2000);

    return () => clearTimeout(timer);
  }, [open, isRedirecting, query, onRedirectComplete, onClose]);

  const handleContinue = () => {
    setIsRedirecting(true);
  };

  const handleDialogClose = () => {
    if (!isRedirecting) {
      onClose();
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={handleDialogClose}
      title="Explore in Azure Data Explorer"
      maxWidth="sm"
      disableBackdropClick={isRedirecting}
      disableEscapeKeyDown={isRedirecting}
      titleSx={{
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #e0e0e0",
        fontWeight: 600,
        fontSize: "1.25rem",
        color: "#333"
      }}
      contentSx={{
        padding: 4,
        textAlign: "center",
        minHeight: 160,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#ffffff"
      }}
      actionsSx={{
        justifyContent: "center",
        padding: 3,
        backgroundColor: "#ffffff",
        borderTop: "1px solid #e0e0e0"
      }}
      actions={
        isRedirecting ? null : (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleDialogClose}
              startIcon={<CloseIcon />}
              sx={{
                minWidth: 80,
                textTransform: "none",
                borderColor: "#ccc",
                color: "#666"
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleContinue}
              disabled={!query.trim()}
              startIcon={<LaunchIcon />}
              sx={{
                minWidth: 120,
                textTransform: "none",
                backgroundColor: "#0078d4",
                "&:hover": {
                  backgroundColor: "#106ebe"
                }
              }}
            >
              Open Now
            </Button>
          </Box>
        )
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
        {!isRedirecting && (
          <Box sx={{ textAlign: "center", maxWidth: 480 }}>
            <Typography variant="body1" gutterBottom sx={{ color: "#333", fontSize: "1rem", lineHeight: 1.5, mb: 3 }}>
              You will be redirected to Azure Data Explorer in {countdown} seconds.
            </Typography>

            {/* Prerequisites Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
              {/* VPN Connection Required */}
              <Chip
                icon={<VpnIcon />}
                label="Azure VPN Connection Required"
                variant="outlined"
                sx={{
                  borderColor: "#ff9800",
                  color: "#ff9800",
                  backgroundColor: "#fff8e1",
                  borderRadius: "20px",
                  height: "40px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    color: "#ff9800",
                    fontSize: "1.2rem"
                  },
                  "& .MuiChip-label": {
                    paddingLeft: "8px",
                    paddingRight: "16px"
                  }
                }}
              />

              {/* Browser Popups Required */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  color: "#666"
                }}
              >
                <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.4 }}>
                  Browser popups must be enabled for this site
                </Typography>
                <InfoIcon
                  sx={{
                    fontSize: "1rem",
                    color: "#2196f3",
                    cursor: "pointer"
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {isRedirecting && (
          <>
            <CircularProgress size={32} sx={{ color: "#0078d4" }} />
            <Typography variant="body1" sx={{ color: "#0078d4", fontWeight: 500 }}>
              Redirecting to Azure Data Explorer...
            </Typography>
          </>
        )}
      </Box>
    </CustomDialog>
  );
};
